// CodeLookupModal — "the customer told me BG-7F3K9PXY, what is it?"
//
// Channel-feed search is scoped to the currently-open channel. This
// widget hits GET /api/posts/by-code/:code directly so a reseller can
// find a card regardless of which channel it was originally posted in.
//
// Inputs: a single text field. Trims whitespace, uppercases, normalizes.
// On Enter (or Найти tap) fires the lookup. Result preview shows the
// hydrated post — image thumbnail, brand, title, channel slug, plus
// either the source URL (premium) or the "Источник в Premium" pill
// (free tier). The "Перейти" button navigates to that channel via the
// onJumpToChannel callback the parent threads in.
//
// The API endpoint validates the format and returns 400 / 404; we
// surface both as a friendly "Не найдено" message.
import { useEffect, useRef, useState } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Btn, Mono, Modal } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT } from "../i18n.jsx";

// Same alphabet as the server-side and Python helper. Used purely
// to give the input a softer client-side validation; the server is
// the source of truth.
const CODE_RE = /^[2-9A-HJ-NP-Z]{4,12}$/;

export function CodeLookupModal({ onClose, onJumpToChannel, onOpenPremiumSettings }) {
  const toast = useToast();
  const t = useT();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [post, setPost] = useState(null);
  const inputRef = useRef(null);

  // Autofocus the input on open so the reseller can immediately type
  // (or paste) the code their customer sent.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const normalize = (s) => (s || "").toUpperCase().replace(/[^2-9A-HJ-NP-Z]/g, "");

  const lookup = async () => {
    const c = normalize(code);
    if (!CODE_RE.test(c)) {
      setError(t("code.lookup.err.format"));
      return;
    }
    setLoading(true);
    setError("");
    setPost(null);
    try {
      const p = await api.getPostByCode(c);
      setPost(p);
    } catch (e) {
      // The API returns 404 with a JSON body; api.js throws Error with
      // the message. "No post with that code" or "Invalid code format"
      // are the two we expect — both render as "Не найдено" to the user.
      const msg = (e?.message || "").toLowerCase();
      if (msg.includes("not") || msg.includes("invalid") || msg.includes("no post")) {
        setError(t("code.lookup.err.notFound"));
      } else {
        setError(e?.message || t("code.lookup.err.generic"));
      }
    } finally {
      setLoading(false);
    }
  };

  const jump = () => {
    if (!post) return;
    const slug = post.channel?.slug;
    if (slug) {
      onJumpToChannel?.(slug);
      onClose();
    } else {
      toast.error(t("code.lookup.err.noChannel"));
    }
  };

  return (
    <Modal title={t("code.lookup.title")} onClose={onClose} width={500}>
      <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, lineHeight: 1.55,
                  marginTop: 0, marginBottom: 14 }}>
        {t("code.lookup.hint")}
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") lookup(); }}
          placeholder={t("code.lookup.placeholder")}
          maxLength={12}
          spellCheck={false}
          autoCapitalize="characters"
          autoCorrect="off"
          style={{
            flex: 1,
            padding: "10px 14px",
            border: `1px solid ${T_NEW.ink12}`,
            borderRadius: T_NEW.r1,
            fontFamily: T_NEW.fontMono,
            fontSize: T_NEW.body,
            background: T_NEW.paperRaised,
            color: T_NEW.ink,
            textTransform: "uppercase",
            letterSpacing: 1.5,
          }}
        />
        <Btn variant="accent" onClick={lookup} disabled={loading}>
          {loading ? t("code.lookup.finding") : t("code.lookup.find")}
        </Btn>
      </div>

      {error && (
        <div style={{
          background: T_NEW.persimmonSoft,
          color: T_NEW.persimmon,
          padding: "10px 12px",
          borderRadius: T_NEW.r1,
          marginBottom: 12,
          fontSize: T_NEW.meta,
        }}>
          {error}
        </div>
      )}

      {post && (
        <div style={{
          border: `1px solid ${T_NEW.ink12}`,
          borderRadius: T_NEW.r2,
          padding: 14,
          background: T_NEW.paperSoft,
        }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            {post.images?.[0]?.url ? (
              <img src={post.images[0].url} alt=""
                   style={{ width: 80, height: 80, objectFit: "cover",
                            borderRadius: T_NEW.r1, flexShrink: 0,
                            background: T_NEW.paperRaised }} />
            ) : (
              <div style={{ width: 80, height: 80, background: T_NEW.paperRaised,
                            borderRadius: T_NEW.r1, flexShrink: 0 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              {post.brand && (
                <Mono color={T_NEW.ink56} size={T_NEW.micro}
                      style={{ display: "block", marginBottom: 4 }}>
                  {post.brand}
                </Mono>
              )}
              <div style={{ fontSize: T_NEW.body, color: T_NEW.ink,
                            lineHeight: 1.35, marginBottom: 6,
                            display: "-webkit-box", WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {post.title}
              </div>
              <Mono color={T_NEW.ink56} size={T_NEW.micro}
                    style={{ display: "block" }}>
                {post.channel?.slug || "—"}
              </Mono>
            </div>
          </div>

          {/* Source-URL line — premium-gated, mirrors the channel-feed
              pattern. Free tier shows a clickable upgrade pill. */}
          <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
            {post.sourceUrl ? (
              <a href={post.sourceUrl} target="_blank" rel="noopener noreferrer"
                 style={{ color: T_NEW.ink56, fontSize: T_NEW.meta,
                          fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                          letterSpacing: T_NEW.monoTrack, textDecoration: "none" }}>
                {t("code.lookup.source")}
              </a>
            ) : null /* premium upsell removed — no source teaser */}
            <span style={{ flex: 1 }} />
            <Btn variant="accent" onClick={jump}>{t("code.lookup.jump")}</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}
